import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {GetPipelineConfigSpecType, PipelineConfigJsonSpec, isStringSpec} from 'pipelines/StepShapeUtils';
import {ButtonSize} from 'ui-library/buttons/Button';
import {GRAY_LIGHTER} from 'ui-library/Colors';
import {JSONObject, JSONValue} from 'util/Types';

import jsonSpecToInitialValue from '../initial-values/jsonSpecToInitialValue';
import {setValueInConfig, maybeGetValueFromConfig} from '../step-config-settings/StepConfigSettingsUtils';
import {useStepConfigEditorContext} from '../StepConfigEditorContext';
import {
  ConfigShapePath,
  FieldUISchemaWithStandardComponentV2,
  ON_CHANGE_CONFIG_MIDDLEWARE_KEY,
  ShapeUISchemaV2,
  TaggedUnionFieldSettingsV2,
} from '../StepConfigEditorTypes';
import StepConfigEditorRouterV2 from './StepConfigEditorRouterV2';
import StepConfigFieldsTemplate from './StepConfigFieldsTemplate';
import {TagChoiceDropdown} from './TaggedUnionEditor';

export interface TaggedUnionEditorV2Props {
  config: JSONObject;
  configShape: GetPipelineConfigSpecType<'taggedUnion'>;
  configShapePath: ConfigShapePath;
  onChangeConfig: (config: JSONObject, path: ConfigShapePath, newValue: JSONValue) => void;
  fieldSettings?: TaggedUnionFieldSettingsV2;
  disabled?: boolean;
}

export default function TaggedUnionEditorV2({
  config,
  configShape,
  configShapePath,
  onChangeConfig,
  fieldSettings,
  disabled,
}: TaggedUnionEditorV2Props): JSX.Element {
  const {dataFramesInfos} = useStepConfigEditorContext();

  const tagKey = configShape.tagKey;
  const selectedTag = maybeGetValueFromConfig(config, [...configShapePath, tagKey]) as string | null;

  const selectedChoice =
    selectedTag != null
      ? (configShape.choices.find((choice) => {
          const tagFieldSpec = choice.fields[tagKey];
          return isStringSpec(tagFieldSpec) && tagFieldSpec.value === selectedTag;
        }) ?? null)
      : null;

  function handleTagChange(choiceShape: GetPipelineConfigSpecType<'shape'>): void {
    const initialValue = jsonSpecToInitialValue(choiceShape, undefined);
    if (fieldSettings?.[ON_CHANGE_CONFIG_MIDDLEWARE_KEY]) {
      const currentValue = maybeGetValueFromConfig(config, configShapePath);
      const transformed = fieldSettings[ON_CHANGE_CONFIG_MIDDLEWARE_KEY](
        initialValue,
        configShapePath,
        currentValue,
        dataFramesInfos
      );
      onChangeConfig(config, configShapePath, transformed);
    } else {
      onChangeConfig(config, configShapePath, initialValue);
    }
  }

  function handleFieldChange(_updatedConfig: JSONObject, path: ConfigShapePath, newValue: JSONValue): void {
    const currentTaggedUnionValue =
      (maybeGetValueFromConfig(config, configShapePath) as JSONObject | null) ?? {};
    const updatedTaggedUnion = setValueInConfig(currentTaggedUnionValue, path, newValue);
    if (fieldSettings?.[ON_CHANGE_CONFIG_MIDDLEWARE_KEY]) {
      const transformed = fieldSettings[ON_CHANGE_CONFIG_MIDDLEWARE_KEY](
        updatedTaggedUnion,
        configShapePath,
        maybeGetValueFromConfig(config, configShapePath),
        dataFramesInfos
      );
      onChangeConfig(config, configShapePath, transformed);
    } else {
      onChangeConfig(config, configShapePath, updatedTaggedUnion);
    }
  }

  const tagField = {
    variableName: fieldSettings?.tagKeyDisplayName ?? tagKey,
    infoText: fieldSettings?.infoText,
    editor: (
      <TagChoiceDropdown
        configShape={configShape}
        onChangeChoice={handleTagChange}
        selectedTag={selectedTag}
        disabled={disabled}
        uiSchema={
          fieldSettings
            ? {
                tagKeyDisplayNames: fieldSettings.tagKeyDisplayNames,
                placeholder: fieldSettings.placeholder,
                renderCyclingSelector: fieldSettings.renderCyclingSelector,
              }
            : undefined
        }
        buttonSize={ButtonSize.SMALL}
      />
    ),
  };

  const choiceFields = getChoiceFields({
    selectedChoice,
    tagKey,
    config,
    configShapePath,
    onChangeConfig: handleFieldChange,
    disabled,
    fieldSettings,
  });

  return (
    <div className={css(styles.taggedUnionWrapper)}>
      <StepConfigFieldsTemplate
        numColumns={1}
        fields={selectedChoice == null ? [tagField] : [tagField, ...choiceFields]}
      />
    </div>
  );
}

function getChoiceFields({
  selectedChoice,
  tagKey,
  config,
  configShapePath,
  onChangeConfig,
  disabled,
  fieldSettings,
}: {
  selectedChoice: GetPipelineConfigSpecType<'shape'> | null;
  tagKey: string;
  config: JSONObject;
  configShapePath: ConfigShapePath;
  onChangeConfig: (config: JSONObject, path: ConfigShapePath, newValue: JSONValue) => void;
  disabled?: boolean;
  fieldSettings?: TaggedUnionFieldSettingsV2;
}): Array<{
  variableName?: string;
  infoText?: string;
  editor: JSX.Element;
}> {
  if (selectedChoice == null) {
    return [];
  }

  const choiceUISchema = fieldSettings?.choiceUISchema;
  const fieldOrder = fieldSettings?.choiceFieldOrder;

  const fieldEntries = Object.entries(selectedChoice.fields).filter(([fieldName]) => fieldName !== tagKey);

  const sortedEntries = fieldOrder ? sortByFieldOrder(fieldEntries, fieldOrder) : fieldEntries;

  const taggedUnionValue = (maybeGetValueFromConfig(config, configShapePath) as JSONObject | null) ?? {};

  return sortedEntries.map(([fieldName, fieldSpec]) => {
    const fieldUI = choiceUISchema?.[fieldName];

    const uiSchema: FieldUISchemaWithStandardComponentV2 | ShapeUISchemaV2 = fieldUI ?? {
      displayName: fieldName,
      configShapePath: [fieldName],
    };

    return {
      variableName: fieldUI?.displayName ?? fieldName,
      infoText: fieldUI?.infoText,
      editor: (
        <StepConfigEditorRouterV2
          config={taggedUnionValue}
          configShape={selectedChoice}
          onChangeConfig={onChangeConfig}
          uiSchema={uiSchema}
          disabled={disabled}
        />
      ),
    };
  });
}

function sortByFieldOrder(
  entries: Array<[string, PipelineConfigJsonSpec]>,
  fieldOrder: ReadonlyArray<string>
): Array<[string, PipelineConfigJsonSpec]> {
  const fieldOrderIndex = new Map<string, number>();
  fieldOrder.forEach((name, idx) => {
    fieldOrderIndex.set(name, idx);
  });
  const originalIndex = new Map<string, number>();
  entries.forEach(([name], idx) => {
    originalIndex.set(name, idx);
  });
  return [...entries].sort(([a], [b]) => {
    const aIdx = fieldOrderIndex.get(a);
    const bIdx = fieldOrderIndex.get(b);
    const aOrder = aIdx !== undefined ? aIdx : fieldOrder.length + (originalIndex.get(a) ?? 0);
    const bOrder = bIdx !== undefined ? bIdx : fieldOrder.length + (originalIndex.get(b) ?? 0);
    return aOrder - bOrder;
  });
}

const styles = StyleSheet.create({
  taggedUnionWrapper: {
    backgroundColor: GRAY_LIGHTER,
    borderRadius: 4,
    padding: 8,
    marginBottom: 8,
  },
});
